import type { LoaderFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type { ShouldRevalidateFunction } from 'react-router';
import { z } from 'zod';
import { getContactSuggestions } from '~/modules/chat/api/get-contact-suggestions.server';

export const CONTACT_SUGGESTIONS_API_ROUTE =
    '/remix-api/chat/contact-suggestions';

const QUERY_SCHEMA = z.object({
    q: z.string().nullish(),
    excludeMemberIDs: z.array(z.coerce.number().int().positive()).nullish(),
    limit: z.coerce.number().int().positive().max(100).nullish(),
    offset: z.coerce.number().int().nonnegative().nullish(),
});

/**
 * API endpoint to get contact suggestions for the current user.
 * Use with the `useContactSuggestions` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-contact-suggestions.ts}
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        q: url.searchParams.get('q'),
        excludeMemberIDs: url.searchParams.getAll('excludeMemberIDs'),
        limit: url.searchParams.get('limit'),
        offset: url.searchParams.get('offset'),
    });

    if (!result.success) {
        return routerData(null, { status: 400 });
    }

    try {
        const suggestions = await getContactSuggestions({
            context,
            search: result.data.q ?? undefined,
            excludeMemberIDs: result.data.excludeMemberIDs ?? undefined,
            limit: result.data.limit ?? undefined,
            offset: result.data.offset ?? undefined,
        });

        if (!suggestions) {
            return routerData(null, { status: 500 });
        }

        return { suggestions };
    } catch (error) {
        logger.error(`Error in ${CONTACT_SUGGESTIONS_API_ROUTE}`, { error });
        return routerData(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
